import { ApolloError, useMutation, useQuery } from '@apollo/client';
import {
  Mutation,
  MutationDeleteVisitorByIdArgs,
  Query,
  QueryGetVisitorByIdArgs
} from 'graphql/generated/graphqlTypes';
import { DELETE_VISITOR_BY_ID } from 'graphql/v2/mutations';
import { GET_ALL_VISITORS_VISITOR_GROUPS, GET_VISITOR_DETAILS } from 'graphql/v2/queries';
import { useEffect } from 'react';
import { refetchQueries } from 'v2/hooks/useAllVisitors/useAllVisitors';
import { TranslatedHookProps } from '../../../shared/types';

interface Props extends TranslatedHookProps {
  visitorId?: string;
}

export const isVisitorNotFoundError = (error?: ApolloError) =>
  error?.graphQLErrors.some((graphQLError) => 'errorType' in graphQLError && graphQLError.errorType === 'UserError') ??
  false;

const useVisitorDetails = ({ handleFetchError, translation, visitorId }: Props) => {
  const {
    loading: visitorDetailsLoading,
    data: visitorDetailsData,
    error: visitorDetailsError,
    refetch: refetchVisitorDetails
  } = useQuery<Pick<Query, 'getVisitorById'>, QueryGetVisitorByIdArgs>(GET_VISITOR_DETAILS, {
    variables: { id: visitorId! },
    skip: visitorId === undefined
  });

  const isVisitorNotFound = isVisitorNotFoundError(visitorDetailsError);

  useEffect(() => {
    if (visitorDetailsError) {
      // A missing visitor already renders the 404 page, so no error snackbar is needed here.
      if (!isVisitorNotFound) {
        handleFetchError(translation.user_details_error_fetching);
      }
      console.error(visitorDetailsError);
    }
  }, [visitorDetailsError]);

  const {
    loading: visitorGroupsLoading,
    data: visitorGroupsData,
    error: visitorGroupsError
  } = useQuery<Pick<Query, 'getVisitorGroupsForTenant'>>(GET_ALL_VISITORS_VISITOR_GROUPS);

  useEffect(() => {
    if (visitorGroupsError) {
      handleFetchError(translation.user_groups_error_fetching);
      console.error(visitorGroupsError);
    }
  }, [visitorGroupsError]);

  const [deleteVisitorMutation, { loading: deleteVisitorLoading }] = useMutation<
    Pick<Mutation, 'deleteVisitorById'>,
    MutationDeleteVisitorByIdArgs
  >(DELETE_VISITOR_BY_ID, {
    refetchQueries,
    awaitRefetchQueries: true,
    onError: (error) => {
      handleFetchError(translation.external_groups_user_error_deleting);
      console.error(error);
    }
  });

  const deleteVisitor = async (visitorId: string) => {
    const { data } = await deleteVisitorMutation({
      variables: { visitorId }
    });
    return data;
  };

  return {
    visitorDetails: visitorDetailsData?.getVisitorById,
    loadingVisitorDetails: visitorDetailsLoading,
    isVisitorNotFound,
    hasVisitorFetchError: Boolean(visitorDetailsError) && !isVisitorNotFound,
    retryFetchingVisitorDetails: () => refetchVisitorDetails(),
    allVisitorsVisitorGroups: visitorGroupsData?.getVisitorGroupsForTenant,
    loadingAllVisitorsVisitorGroups: visitorGroupsLoading,
    deleteVisitor,
    deleteVisitorLoading
  };
};

export default useVisitorDetails;
